import { pageFaqAdditions } from "@/data/pageFaqs";
import { PageFaqs } from "@/components/site/PageFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { m, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Clock, Stethoscope } from "lucide-react";
import { BookFitCall } from "@/components/site/BookFitCall";
import { absoluteUrl, canonicalLinks, ogUrl, ogImage, jsonLd, breadcrumbSchema, faqPageSchema, webPageSchema, scanAppSchema } from "@/lib/seo";
import { SCAN_DESCRIPTION, SCAN_QUESTION_COUNT, SCAN_DURATION_MINUTES } from "@/lib/diagnostic";
import { DiagramFrame, rise } from "@/components/diagrams/DiagramFrame";
import { PhotoBand } from "@/components/site/PhotoBand";
import { tools, toolTemplates, toolBySlug } from "@/data/tools";
import { toolAppId } from "@/components/site/tools/ToolPageShell";
import { Calculator, FileSpreadsheet } from "lucide-react";
import operatingScorecardImg from "@/assets/photos/operating-scorecard.webp";

const hubDescription = `${tools.length} free tools: cash runway, price change, time and accountability, COO cost, revenue leakage, scorecard and partner alignment, plus the Operating Scan.`;

const faqs = pageFaqAdditions["/tools"];

export const Route = createFileRoute("/tools/")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Tools and Templates: free business calculators | Ajility" },
      {
        name: "description",
        content:
          hubDescription,
      },
      { property: "og:title", content: "Tools and Templates: free business calculators | Ajility" },
      {
        property: "og:description",
        content:
          hubDescription,
      },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: "Tools and Templates: free business calculators | Ajility" },
      { name: "twitter:description", content: hubDescription },
      ...ogImage("tools", "Tools and Templates by Ajility"),
      ogUrl("/tools"),
    ],
    links: canonicalLinks("/tools"),
    scripts: [
      jsonLd(faqPageSchema("/tools", faqs)),
      jsonLd(breadcrumbSchema([{ name: "Tools and Templates", path: "/tools" }])),
      jsonLd({
        "@context": "https://schema.org",
        "@type": "ItemList",
        "@id": `${absoluteUrl("/tools")}#tools`,
        name: "Free calculators and tools",
        itemListElement: tools.map((t, k) => ({
          "@type": "ListItem",
          position: k + 1,
          name: t.name,
          url: absoluteUrl(t.path),
          item: { "@id": toolAppId(t.slug) },
        })),
      }),
      jsonLd(scanAppSchema()),
      jsonLd(webPageSchema({ image: "tools",
        path: "/tools",
        name: "Tools and Templates: free business calculators | Ajility",
        description: hubDescription,
      })),
    ],
  }),
  component: ToolsPage,
});

const assessments = [
  {
    icon: Stethoscope,
    name: "Free Operating Scan",
    points: `${SCAN_QUESTION_COUNT}-statement`,
    description: `${SCAN_DESCRIPTION} Open to anyone, no email needed to see the score.`,
    time: `${SCAN_DURATION_MINUTES} min`,
    badge: "Free",
    to: "/scan" as const,
    cta: "Take the scan",
  },
  {
    icon: Stethoscope,
    name: "Team Survey",
    points: "30-statement",
    description:
      "30 statements, a subset of the same bank, answered by frontline staff individually inside an engagement. Covers eight of the nine operating pillars; Money and Cash is asked of leadership only. Answers are private and reports show cohort averages only.",
    time: "About nine minutes",
    badge: "Within an engagement",
    to: "/contact" as const,
    cta: "Book a fit call",
  },
  {
    icon: Stethoscope,
    name: "Leadership Survey",
    points: "60-statement",
    description:
      "60 statements, the full bank, answered individually by the owners and senior managers inside an engagement. Produces the leadership report and the group report, which shows where the leadership team disagrees with itself and with the floor.",
    time: "About 18 minutes",
    badge: "Within an engagement",
    to: "/contact" as const,
    cta: "Book a fit call",
  },
];

function ScanWheel({ className }: { className?: string }) {
  const reduced = useReducedMotion() ?? false;
  const areas = [
    "Direction",
    "Leadership",
    "People",
    "Rhythm",
    "Numbers",
    "Systems",
  ];
  const cx = 480;
  const cy = 260;
  const radius = 150;
  const hubR = 62;
  const boxW = 130;
  const boxH = 54;

  return (
    <DiagramFrame
      className={className}
      caption="The operating areas behind the provisional score. Your result names the weakest areas in your own business."
      captionClassName="italic"
    >
      <svg
        viewBox="260 65 440 390"
        role="img"
        aria-labelledby="wheel-title wheel-desc"
        className="h-auto w-full max-w-full"
      >
        <title id="wheel-title">Operating areas assessed by the Operating Scan</title>
        <desc id="wheel-desc">
          Six operating areas arranged in a wheel around a central Operating Scan hub:
          Direction, Leadership, People, Rhythm, Numbers and Systems.
        </desc>

        <ellipse
          cx={cx}
          cy={cy}
          rx={radius + 36}
          ry={radius + 36}
          className="stroke-border"
          strokeWidth="2"
          strokeDasharray="8 6"
          fill="none"
        />

        {areas.map((a, i) => {
          const angle = (Math.PI * 2 * i) / areas.length - Math.PI / 2;
          const innerX = cx + hubR * Math.cos(angle);
          const innerY = cy + hubR * Math.sin(angle);
          const outerX = cx + (radius - 16) * Math.cos(angle);
          const outerY = cy + (radius - 16) * Math.sin(angle);
          return (
            <line
              key={`spoke-${a}`}
              x1={innerX}
              y1={innerY}
              x2={outerX}
              y2={outerY}
              className="stroke-border"
              strokeWidth="2"
            />
          );
        })}

        <m.g {...rise(reduced, 0)}>
          <circle cx={cx} cy={cy} r={hubR} className="fill-midnight" />
          <text
            x={cx}
            y={cy - 6}
            textAnchor="middle"
            fontSize="15"
            fontWeight="600"
            className="fill-frost"
          >
            Operating
          </text>
          <text
            x={cx}
            y={cy + 18}
            textAnchor="middle"
            fontSize="15"
            fontWeight="600"
            className="fill-frost"
          >
            Scan
          </text>
        </m.g>

        {areas.map((a, i) => {
          const angle = (Math.PI * 2 * i) / areas.length - Math.PI / 2;
          const x = cx + radius * Math.cos(angle);
          const y = cy + radius * Math.sin(angle);
          return (
            <m.g key={a} {...rise(reduced, i * 0.06)}>
              <rect
                x={x - boxW / 2}
                y={y - boxH / 2}
                width={boxW}
                height={boxH}
                rx="12"
                className="fill-card stroke-border"
                strokeWidth="2"
              />
              <text
                x={x}
                y={y + 5}
                textAnchor="middle"
                fontSize="15"
                fontWeight="600"
                className="fill-foreground"
              >
                {a}
              </text>
            </m.g>
          );
        })}
      </svg>
    </DiagramFrame>
  );
}

function ToolsPage() {
  return (
    <>
      {/* Hero */}
      <section className="bg-midnight pb-20 pt-32 text-frost">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-3xl">
            <m.p
              initial={{ y: 12 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-sm font-semibold uppercase tracking-widest text-silver"
            >
              Free tools, no login
            </m.p>
            <m.h1
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl"
            >
              Tools and Templates
            </m.h1>
            <m.p
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 text-lg leading-relaxed text-frost/80"
            >
              Free diagnostics and calculators for owners and leaders of Australian businesses between $3M and $30M. Start with the Operating Scan, or answer one question at a time with a calculator.
            </m.p>
            <m.div
              initial={{ y: 16 }}
              animate={{ y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-10 flex flex-col items-start gap-4 sm:flex-row"
            >
              <Link
                to="/scan"
                className="inline-flex h-12 items-center gap-2 rounded-full bg-frost px-7 text-base font-semibold text-midnight-deep transition hover:bg-white"
              >
                Take the scan
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
              <BookFitCall
                placement="tools_page_hero"
                buttonClassName="inline-flex h-12 items-center gap-2 rounded-full border border-white/20 px-7 text-base font-semibold text-frost transition hover:border-steel hover:text-frost"
                noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
              >
                Book a fit call
              </BookFitCall>
            </m.div>
          </div>
        </div>
      </section>

      {/* Assessments */}
      <section className="bg-surface py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <h2 className="mb-4 font-serif text-3xl text-midnight sm:text-4xl">Diagnostics</h2>
          <p className="mb-10 max-w-3xl text-charcoal">Start with a quick read of the business or choose a deeper review when the decisions need more evidence.</p>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {assessments.map((d, i) => (
              <m.div
                key={d.name}
                initial={{ y: 20 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="flex flex-col rounded-3xl border border-border bg-card p-8 shadow-soft"
              >
                <div className="flex items-start justify-between">
                  <div className="grid h-12 w-12 place-items-center rounded-2xl bg-midnight text-steel-on-dark">
                    <d.icon className="h-6 w-6" />
                  </div>
                  <span className="rounded-full bg-midnight/10 px-3 py-1 text-xs font-semibold text-midnight">
                    {d.badge}
                  </span>
                </div>

                <p className="mt-6 text-sm font-semibold uppercase tracking-wider text-steel">
                  {d.points}
                  <span className="ml-1 font-normal normal-case text-charcoal">assessment</span>
                </p>
                <h3 className="mt-2 font-serif text-2xl text-midnight">{d.name}</h3>
                <p className="mt-3 flex-1 text-charcoal leading-relaxed">
                  {d.description}
                </p>

                <div className="mt-8 space-y-4">
                  <div className="flex flex-wrap gap-3 text-sm text-charcoal">
                    <span className="flex items-center gap-1.5 rounded-full bg-surface px-3 py-1">
                      <Clock className="h-3.5 w-3.5" />
                      {d.time}
                    </span>
                  </div>
                  {d.cta === "Book a fit call" ? (
                    <BookFitCall
                      placement={`tools_card_${d.name.toLowerCase().replace(/\s+/g, "_")}`}
                      buttonClassName="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-midnight text-sm font-semibold text-frost transition hover:bg-midnight-deep"
                      noteClassName="mt-2 text-center text-sm font-medium text-charcoal/80 hover:text-midnight"
                    >
                      {d.cta}
                      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </BookFitCall>
                  ) : (
                    <Link
                      to={d.to}
                      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-midnight text-sm font-semibold text-frost transition hover:bg-midnight-deep"
                    >
                      {d.cta}
                      <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                  )}
                </div>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      {/* Calculators */}
      <section className="bg-background py-20 lg:py-24" aria-labelledby="calc-h">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 id="calc-h" className="font-serif text-3xl text-midnight sm:text-4xl">Calculators and tools</h2>
          <p className="mt-4 max-w-3xl text-charcoal">{tools.length} free tools. Each shows the answer straight away; a one page report and PDF are one short form away.</p>
          <ul className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {tools.map((t) => (
              <li key={t.slug}>
                <Link to={t.path} className="card-panel card-interactive flex h-full flex-col">
                  <Calculator className="h-6 w-6 text-steel" aria-hidden="true" />
                  <h3 className="mt-4 font-serif text-2xl text-midnight">{t.name}</h3>
                  <p className="mt-2 flex-1 text-charcoal">{t.oneLine}</p>
                  <div className="mt-6 flex flex-wrap gap-2 text-sm text-charcoal">
                    <span className="flex items-center gap-1.5 rounded-full bg-surface px-3 py-1"><Clock className="h-3.5 w-3.5" aria-hidden="true" />{t.time}</span>
                    <span className="rounded-full bg-midnight/10 px-3 py-1 font-semibold text-midnight">Free, no login</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Templates */}
      <section className="bg-surface py-20 lg:py-24" aria-labelledby="tpl-h">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <h2 id="tpl-h" className="font-serif text-3xl text-midnight sm:text-4xl">Templates</h2>
          <ul className="mt-10 grid gap-6 md:grid-cols-3">
            {toolTemplates.map((t) => (
              <li key={t.name} className="card-panel flex flex-col">
                <div className="flex items-start justify-between">
                  <FileSpreadsheet className="h-6 w-6 text-steel" aria-hidden="true" />
                  <span className="rounded-full bg-gold/25 px-3 py-1 text-xs font-semibold text-midnight">Coming soon</span>
                </div>
                <h3 className="mt-4 font-serif text-xl text-midnight">{t.name}</h3>
                <p className="mt-2 text-charcoal">
                  Sent with your report from the matching tool, the{" "}
                  <Link to={toolBySlug(t.tool).path} className="font-medium text-midnight underline underline-offset-4">{toolBySlug(t.tool).name}</Link>.
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* SCAN WHEEL */}
      <section className="bg-surface py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <ScanWheel />
        </div>
      </section>

      <PhotoBand
        src={operatingScorecardImg}
        alt="A printed operating scorecard beside a whiteboard tracking grid with markers"
        surface="background"
      />

      <PageFaqs faqs={faqs} />

      {/* CTA */}
      <section className="bg-midnight py-20 text-frost lg:py-24">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <h2 className="font-serif text-3xl md:text-4xl">
             Not sure which operations assessment fits?
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-frost/80">
             Tell Ajility what is happening in the business and get a clear view on the right starting point.
          </p>
          <div className="mt-8">
            <BookFitCall
              placement="tools_page_footer"
              buttonClassName="inline-flex h-12 items-center gap-2 rounded-full bg-frost px-7 text-base font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
            </BookFitCall>
          </div>
        </div>
      </section>
    </>
  );
}
